<template>
  <div class="blissrose-datasets">
    <!-- 块一：数据集版本（版本平铺） -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <span>数据集版本</span>
        <div class="table-header-right">
          <!-- 数据集维：批次、版本、物化、训练引用全按它走；不选＝默认数据集 -->
          <el-select
            v-model="datasetId"
            class="dataset-picker"
            size="small"
            placeholder="默认数据集"
            @change="onDatasetChange"
          >
            <el-option
              v-for="d in datasets"
              :key="d.id"
              :label="d.name ? `${d.name}（${d.slug}）` : d.slug"
              :value="d.id"
            />
          </el-select>
          <el-button size="small" icon="el-icon-plus" @click="openDatasetDialog">新建数据集</el-button>
          <el-button size="small" icon="el-icon-upload2" @click="openRegisterDialog">登记批次</el-button>
          <el-button size="small" icon="el-icon-tickets" @click="openBatchLog">批次记录</el-button>
          <el-button size="small" icon="el-icon-price-tag" @click="openClassDialog">类别管理</el-button>
          <el-input
            v-model="keyword"
            class="keyword-input"
            size="small"
            placeholder="按版本筛选"
            prefix-icon="el-icon-search"
            clearable
          />
          <span class="total-count">共 {{ filteredVersions.length }} 个版本</span>
          <el-tooltip
            :disabled="!publishingVersion"
            :content="publishingVersion ? `v${publishingVersion} 正在发布，完成后再试` : ''"
            placement="top"
          >
            <span>
              <el-button
                type="primary"
                size="small"
                icon="el-icon-plus"
                :disabled="!!publishingVersion"
                @click="openPublish"
              >发布版本</el-button>
            </span>
          </el-tooltip>
          <el-button size="small" icon="el-icon-refresh" :loading="loadingVersions" @click="loadVersions">刷新</el-button>
        </div>
      </div>

      <el-table
        v-loading="loadingVersions"
        :data="filteredVersions"
        stripe
        border
        size="small"
        style="width:100%"
      >
        <el-table-column label="版本" width="90">
          <template slot-scope="{ row }">
            <span class="mono"><b>v{{ dash(row.version) }}</b></span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="92" align="center">
          <template slot-scope="{ row }">
            <el-tooltip v-if="versionStatusMeta(row).hint" :content="versionStatusMeta(row).hint" placement="top">
              <el-tag :type="versionStatusMeta(row).tag" size="mini">{{ versionStatusMeta(row).label }}</el-tag>
            </el-tooltip>
            <el-tag v-else :type="versionStatusMeta(row).tag" size="mini">{{ versionStatusMeta(row).label }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="标注项目" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <!-- 项目名直达标注平台（链接地址由平台按配置拼好）；无项目时不放假编号，悬停给出缘由 -->
            <template v-if="projectItems(row).length">
              <a
                v-for="(p, i) in projectItems(row)"
                :key="p.id"
                class="ls-link"
                :title="p.name"
                :href="p.url"
                target="_blank"
                rel="noopener"
              >{{ p.name }}<template v-if="i < projectItems(row).length - 1">、</template></a>
            </template>
            <el-tooltip v-else :content="noProjectReason(row)" placement="top">
              <span class="muted">-</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="帧数 / 类别" width="130">
          <template slot-scope="{ row }">
            <span>{{ row.frame_count }} 帧<template v-if="row.class_count"> · {{ row.class_count }} 类</template></span>
          </template>
        </el-table-column>
        <el-table-column label="训练 / 验证 / 测试" width="150" align="center">
          <template slot="header">
            <span>训练 / 验证 / 测试</span>
            <el-tooltip content="发布时按比例切分的帧数" placement="top">
              <i class="el-icon-info header-hint" />
            </el-tooltip>
          </template>
          <template slot-scope="{ row }">
            <span v-if="row.train_count || row.val_count || row.test_count">{{ row.train_count || 0 }} / {{ row.val_count || 0 }}<template v-if="row.test_count"> / {{ row.test_count }}</template></span>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="大小" width="90" align="center">
          <template slot-scope="{ row }">
            <span v-if="row.bytes">{{ formatBytes(row.bytes) }}</span>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="发布时间" width="150" align="center">
          <template slot-scope="{ row }">
            <span v-if="row.pv">{{ formatTime(row.pv.published_at) }}</span>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="训练使用" width="110" align="center">
          <template slot-scope="{ row }">
            <template v-if="row.pv">
              <el-tag v-if="row.pv.deleted" type="info" size="mini">已移除</el-tag>
              <el-tag v-else-if="row.pv.used_by_training" type="success" size="mini">已训练 {{ row.pv.training_runs.length }} 次</el-tag>
              <el-tag v-else type="warning" size="mini">未使用</el-tag>
            </template>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="导出标注" width="100" align="center">
          <template slot="header">
            <span>导出标注</span>
            <el-tooltip content="标注平台里人工标的框导回数据集、生成 YOLO txt 的状态（流水线步骤，不是下载）" placement="top">
              <i class="el-icon-info header-hint" />
            </el-tooltip>
          </template>
          <template slot-scope="{ row }">
            <template v-if="row.pv && (row.pv.projects || []).length">
              <el-tooltip
                v-if="row.pv.export && row.pv.export.failed"
                :content="row.pv.export.error_message || '有导出任务失败'"
                placement="top"
              >
                <el-tag type="danger" size="mini">失败 {{ row.pv.export.failed }}</el-tag>
              </el-tooltip>
              <el-tag
                v-else-if="row.pv.export && row.pv.export.pending + row.pv.export.processing"
                type="warning"
                size="mini"
              >导出中</el-tag>
              <el-tag v-else-if="row.pv.export" type="success" size="mini">已导出</el-tag>
              <el-tag v-else type="info" size="mini">未导出</el-tag>
            </template>
            <!-- 无标注项目时导出不适用：不给状态也不给入口 -->
            <el-tooltip
              v-else-if="row.pv"
              content="这一版没有关联的标注项目，标注导出不适用"
              placement="top"
            >
              <span class="muted">-</span>
            </el-tooltip>
            <span v-else class="cell-sub">-</span>
          </template>
        </el-table-column>
        <el-table-column label="本地缓存" min-width="220">
          <template slot="header">
            <span>本地缓存</span>
            <el-tooltip content="训练机本地的副本（训练派发时会用到）" placement="top">
              <i class="el-icon-info header-hint" />
            </el-tooltip>
          </template>
          <template slot-scope="{ row }">
            <el-tag :type="cacheMeta(row).tag" size="mini">{{ cacheMeta(row).label }}</el-tag>
            <span v-if="localOf(row)" class="cell-sub">
              {{ formatBytes(localOf(row).bytes) }}<template v-if="localOf(row).finished_at"> · {{ formatTime(localOf(row).finished_at) }}</template>
            </span>
            <span v-else-if="isPulling(row) && pullPercent(row)" class="cell-sub">{{ pullPercent(row) }}</span>
            <!-- 上一次下载失败的原因 -->
            <div v-if="row.cache_error" class="cell-sub danger-text ellipsis-cell" :title="row.cache_error">{{ row.cache_error }}</div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="230" align="center" fixed="right">
          <template slot-scope="{ row }">
            <!-- 读窗口：整版「图 + 标注 txt」预签名清单，前端拼 zip（给人下到本机） -->
            <el-button
              type="text"
              size="mini"
              title="把这一版的图和标注 txt 打包成 zip 下载到本机（浏览器下载）"
              @click="downloadManifest(row)"
            >打包下载</el-button>
            <!-- 没下到训练机的版本给个下载入口（平台立刻返回，落地靠轮询） -->
            <el-button
              v-if="canDownload(row)"
              type="text"
              size="mini"
              title="把这个版本下发到训练机本地缓存，训练派发时用它"
              :loading="postingRef === row.ref"
              @click="handleDownload(row)"
            >下发训练</el-button>
            <!-- 软隐藏：只从列表/训练入口移除，不删版本、shard、label 或缓存 -->
            <el-button
              v-if="row.status !== 'publishing'"
              type="text"
              size="mini"
              title="从版本列表移除（不删除版本数据和缓存，可人工恢复）"
              @click="hideVersion(row)"
            >隐藏</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 块二：缓存回收。扫描与判定由 ml-services 现扫现给 -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <span>本地缓存回收</span>
        <div class="table-header-right">
          <span class="total-count">磁盘剩余 {{ formatBytes(rootFree) }}</span>
          <el-button size="small" icon="el-icon-refresh" :loading="loadingInventory" @click="loadInventory">刷新</el-button>
          <el-button
            type="danger"
            size="small"
            icon="el-icon-delete"
            :disabled="!selectedCount"
            :loading="reclaiming"
            @click="handleReclaim"
          >{{ reclaimLabel }}</el-button>
        </div>
      </div>

      <el-alert
        v-if="factsError"
        class="notice"
        type="warning"
        show-icon
        :closable="false"
        title="无法读取训练机的使用记录，全部保留"
        :description="factsError"
      />

      <div v-if="inventory" class="stat-row">
        <div class="stat">
          <span class="stat-label">缓存内容</span>
          <span class="stat-value">{{ formatBytes(totals.content_bytes) }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">可回收</span>
          <span class="stat-value">{{ formatBytes(totals.reclaimable_bytes) }}</span>
          <span class="cell-sub">{{ totals.reclaimable_versions }} 版 · {{ totals.reclaimable_models }} 份权重</span>
        </div>
        <div class="stat">
          <span class="stat-label">共享内容</span>
          <span class="stat-value">{{ formatBytes(totals.pool_bytes) }}</span>
          <span class="cell-sub">{{ pool.count }} 项</span>
        </div>
        <!-- 政策类保留的版本可手动清理 -->
        <div v-if="totals.forceable_versions" class="stat">
          <span class="stat-label">可手动清理</span>
          <span class="stat-value">{{ totals.forceable_versions }}</span>
          <span class="cell-sub">自动回收不覆盖</span>
        </div>
        <div class="stat">
          <span class="stat-label">无引用内容</span>
          <span class="stat-value">{{ pool.orphan_count }}</span>
          <span class="cell-sub">可清 {{ pool.sweepable_count }} 个 · {{ formatBytes(pool.sweepable_bytes) }}</span>
        </div>
      </div>

      <el-table
        ref="cacheTable"
        v-loading="loadingInventory"
        :data="rows"
        row-key="key"
        stripe
        border
        size="small"
        style="width:100%"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="46" :selectable="selectable" />
        <el-table-column label="类型" width="72" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.kind === 'version' ? 'info' : ''" size="mini" :effect="row.kind === 'version' ? 'plain' : 'light'">
              {{ row.kind === 'version' ? '数据集' : '权重' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="名称" min-width="230" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span class="mono">{{ row.label }}</span>
            <div v-if="row.sub" class="cell-sub">{{ row.sub }}</div>
          </template>
        </el-table-column>
        <el-table-column label="占用" width="100">
          <template slot-scope="{ row }">{{ formatBytes(row.bytes) }}</template>
        </el-table-column>
        <el-table-column label="清理建议" min-width="280">
          <template slot-scope="{ row }">
            <el-tag :type="row.verdict === 'reclaimable' || row.forceable ? 'warning' : 'info'" size="mini">
              {{ verdictLabel(row) }}
            </el-tag>
            <!-- 旧命名目录（版本号是日期串）：自动回收永不覆盖 -->
            <el-tag v-if="row.legacy" class="legacy-tag" size="mini" type="info" effect="plain">旧格式</el-tag>
            <!-- 可清理的行不显示保留理由 -->
            <div v-if="row.reasons.length && !row.forceable" class="cell-sub">{{ row.reasons.join('；') }}</div>
          </template>
        </el-table-column>
        <el-table-column label="最近派发" width="150">
          <template slot-scope="{ row }">{{ formatTime(row.last_dispatched_at) }}</template>
        </el-table-column>
      </el-table>

      <div v-if="inventory && (pool.sweepable_count || pool.orphan_count)" class="pool-row">
        <span class="cell-sub">
          <template v-if="pool.sweepable_count">
            可清 {{ pool.sweepable_count }} 个 · {{ formatBytes(pool.sweepable_bytes) }}<template
              v-if="pool.orphan_count > pool.sweepable_count"
            >；另有 {{ pool.orphan_count - pool.sweepable_count }} 个是
              {{ Math.round((pool.grace_seconds || 0) / 60) }} 分钟内新增的，本轮不动</template>
          </template>
          <template v-else>
            无引用 {{ pool.orphan_count }} 个，均为
            {{ Math.round((pool.grace_seconds || 0) / 60) }} 分钟内新增
          </template>
        </span>
      </div>

      <!-- 只列不删的两组 -->
      <div v-if="pulling.length" class="list-line">
        <span class="list-label">正在下载</span>
        <span class="cell-sub">
          {{ pulling.map(item => `${item.dir}（${formatTime(item.modified_at)}）`).join('、') }}
        </span>
      </div>
      <div v-if="strays.length" class="list-line">
        <span class="list-label">未知文件</span>
        <span class="cell-sub">
          {{ strays.map(item => `${item.name}（${item.reason}）`).join('、') }}
        </span>
      </div>

      <el-alert
        v-if="result"
        class="notice"
        :type="result.skipped.length ? 'warning' : 'success'"
        show-icon
        @close="result = null"
        :title="resultTitle"
      >
        <div v-for="(item, index) in result.deleted" :key="`d${index}`">
          已删除 {{ itemLabel(item) }}，释放 {{ formatBytes(item.bytes) }}
        </div>
        <div v-for="(item, index) in result.skipped" :key="`s${index}`">
          跳过 {{ itemLabel(item) }}：{{ item.reason }}
        </div>
      </el-alert>
    </el-card>

    <!-- 发布版本弹窗：加入/剔除一次生成新版本 -->
    <el-dialog
      title="发布版本"
      :visible.sync="publishDialog.visible"
      :width="publishDialog.job ? '460px' : '1100px'"
      :close-on-click-modal="false"
      @close="onPublishDialogClose"
    >
      <!-- 发布进度视图：提交后 / 重开检测到在途时整窗切换 -->
      <template v-if="publishDialog.job">
        <div class="publish-progress">
          <div class="publish-progress-title">
            <span v-if="publishInFlight"><i class="el-icon-loading" /> 发布中</span>
            <span v-else-if="publishJobStatus === 'completed'"><i class="el-icon-circle-check" /> 发布完成</span>
            <span v-else><i class="el-icon-circle-close" /> 发布失败</span>
            <span class="publish-progress-target">v{{ publishDialog.job.version }}</span>
          </div>
          <p v-if="publishInFlight" class="publish-progress-line">
            {{ publishJobStatus === 'pending' ? '已登记，排队等待执行…' : '正在执行发布事务；大版本耗时长，可以关掉弹窗，稍后回来接着看。' }}
          </p>
          <p v-if="publishJobStatus === 'completed'" class="publish-progress-line publish-progress-ok">{{ publishResultText }}</p>
          <el-alert
            v-if="publishJobStatus === 'failed'"
            type="error"
            :closable="false"
            show-icon
            :title="`发布失败：${publishDialog.job.error_message || '平台没记下原因'}`"
          />
          <p v-if="publishJobStatus === 'failed'" class="publish-progress-line">
            该版本已回滚，重新发起发布即可。
          </p>
          <div v-if="publishPollDead" class="publish-progress-dead">
            <span>进度查询连续失败，已暂停刷新。</span>
            <el-button type="text" size="mini" @click="requeryPublish">重新查询</el-button>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="publish-current">
          当前最新版本：
          <template v-if="currentVersion">
            <b>v{{ currentVersion.version }}</b> 共 {{ currentVersion.frames }} 张
            （train {{ currentVersion.train }} / val {{ currentVersion.val }}<template v-if="currentVersion.test"> / test {{ currentVersion.test }}</template>）
          </template>
          <template v-else>尚无已发布数据</template>
        </div>

        <!-- 条件筛选跨栏置顶；符合条件批次自动勾选到「加入」 -->
        <div class="publish-cond">
          <div class="publish-cond-title">
            <span>条件筛选（符合条件的批次自动勾选到「加入」）</span>
            <span class="publish-cond-fold" @click="publishDialog.condCollapsed = !publishDialog.condCollapsed">
              {{ publishDialog.condCollapsed ? '展开 ▾' : '收起 ▴' }}
            </span>
          </div>
          <div v-show="!publishDialog.condCollapsed">
            <div class="publish-cond-row">
              <span class="publish-cond-lab">标签条件</span>
              <template v-for="g in ['scene', 'weather', 'time', 'lighting']">
                <span class="publish-cond-lab" :key="`l-${g}`">{{ { scene: '场景', weather: '天气', time: '时间', lighting: '光照' }[g] }}</span>
                <el-select
                  :key="`s-${g}`"
                  v-model="publishDialog.cond[g]"
                  placeholder="不限"
                  clearable
                  size="mini"
                  style="width: 110px"
                >
                  <el-option v-for="v in tagGroupOptions[g]" :key="v" :label="v" :value="v" />
                </el-select>
              </template>
              <el-button type="text" size="mini" @click="clearCond">清除</el-button>
            </div>
            <div class="publish-cond-row">
              <span class="publish-cond-lab">标注条件</span>
              <span class="publish-cond-lab">含类别</span>
              <el-select v-model="publishDialog.cond.label" placeholder="不限" clearable size="mini" style="width: 130px">
                <el-option v-for="v in labelOptions" :key="v" :label="v" :value="v" />
              </el-select>
              <span class="publish-cond-lab">实例数 ≥</span>
              <el-input-number v-model="publishDialog.cond.minInstances" :min="1" size="mini" style="width: 100px" />
              <span class="publish-cond-sep"></span>
              <span class="publish-cond-lab">采样</span>
              <el-checkbox v-model="publishDialog.cond.limitBatches" class="publish-cond-check">限制批数</el-checkbox>
              <template v-if="publishDialog.cond.limitBatches">
                <span class="publish-cond-lab">最多发布</span>
                <el-input-number v-model="publishDialog.cond.sampleN" :min="1" size="mini" style="width: 100px" />
                <span class="publish-cond-lab">批（按时间均匀）</span>
              </template>
            </div>
            <div v-if="condActive && sampledMatched" class="publish-cond-note">
              符合条件的批次已自动勾选 {{ publishDialog.addSelected.length }} / {{ condMatched.length }}，可在下方手动调整
            </div>
          </div>
        </div>

        <div class="publish-cols">
          <!-- 加入批次：未加入版本 + 曾移除的批次（勾曾移除的 = 再加回） -->
          <div class="publish-col">
            <div class="publish-col-head">
              <span class="publish-col-title">➕ 加入批次</span>
              <el-select v-model="publishDialog.addProject" placeholder="项目筛选" clearable size="mini" style="width: 132px">
                <el-option v-for="name in addProjectOptions" :key="name" :label="name" :value="name" />
              </el-select>
              <el-select v-model="publishDialog.addStatus" placeholder="批次状态" clearable size="mini" style="width: 118px">
                <el-option label="未加入版本" value="unpublished" />
                <el-option label="曾移除" value="removed" />
              </el-select>
            </div>
            <div class="publish-col-body">
              <el-checkbox-group v-model="publishDialog.addSelected">
                <el-checkbox v-for="b in addCandidates" :key="b.batch" :label="b.batch" class="publish-row">
                  <span class="publish-row-line1">
                    <span class="publish-batch">{{ b.batch }}</span>
                    <span class="publish-sub">{{ (b.job && b.job.project_name) || '-' }} · {{ b.image_count }} 张</span>
                    <span class="publish-conf" :class="{ 'publish-unconf': confirmedGapOf(b) > 0 }">
                      已人工标注 {{ Number(b.annotated_reviewed) || 0 }}/{{ b.image_count }}
                    </span>
                    <el-tag size="mini" :type="addStatusOf(b).type">{{ addStatusOf(b).text }}</el-tag>
                  </span>
                  <span class="publish-row-line2">
                    <template v-if="topTagPairs(b).length > 0">
                      <span v-for="pair in topTagPairs(b)" :key="pair[0]" class="publish-tag">{{ pair[0] }} {{ pair[1] }}%</span>
                    </template>
                    <span v-else class="publish-tags-empty">标签未知</span>
                    <span class="publish-inst">{{ instanceText(b) }}</span>
                  </span>
                </el-checkbox>
              </el-checkbox-group>
              <div v-if="addCandidates.length === 0" class="publish-empty">没有可加入的批次</div>
            </div>
            <div class="publish-col-foot">
              已选 {{ publishDialog.addSelected.length }} 个批次 · {{ addSelectedCount }} 张
            </div>
          </div>

          <!-- 移除批次：当前版本里的批次 -->
          <div class="publish-col">
            <div class="publish-col-head">
              <span class="publish-col-title">➖ 移除批次</span>
              <el-select v-model="publishDialog.removeProject" placeholder="项目筛选" clearable size="mini" style="width: 132px">
                <el-option v-for="name in removeProjectOptions" :key="name" :label="name" :value="name" />
              </el-select>
              <el-select v-model="publishDialog.removeVersion" placeholder="版本筛选" clearable size="mini" style="width: 118px">
                <el-option v-for="v in removeVersionOptions" :key="v" :label="`v${v}`" :value="v" />
              </el-select>
            </div>
            <div class="publish-col-body">
              <el-checkbox-group v-model="publishDialog.removeSelected">
                <el-checkbox v-for="b in removeCandidates" :key="b.batch" :label="b.batch" class="publish-row">
                  <span class="publish-batch">{{ b.batch }}</span>
                  <span class="publish-sub">{{ (b.job && b.job.project_name) || '-' }} · {{ b.image_count }} 张</span>
                  <el-tag size="mini" type="success">v{{ b.added_in_version }}</el-tag>
                </el-checkbox>
              </el-checkbox-group>
              <div v-if="removeCandidates.length === 0" class="publish-empty">当前版本里没有可移除的批次</div>
            </div>
            <div class="publish-col-foot">
              已选 {{ publishDialog.removeSelected.length }} 个批次 · {{ removeSelectedCount }} 张
            </div>
          </div>
        </div>

        <div class="publish-preview" :class="{ 'publish-overlap': overlapBatches.length > 0 }">
          <template v-if="overlapBatches.length > 0">
            ⚠ 批次 {{ overlapBatches.join('、') }} 不能同时加入和移除，请取消一侧勾选
          </template>
          <template v-else>
            <div>
              结果预览：v{{ publishDialog.version }} = {{ currentFrames }} + {{ addSelectedCount }} − {{ removeSelectedCount }}
              = <b>{{ previewFrames }} 张</b>
            </div>
            <div class="publish-preview-inst">加入部分目标实例：{{ instanceSummaryText }}（移除不参与统计）</div>
            <div v-if="addSelectedConfirmed.missing > 0" class="publish-preview-unconf">
              ⚠ 加入的批次里有 {{ addSelectedConfirmed.missing }} 张未人工标注
              （已人工标注 {{ addSelectedConfirmed.reviewed }}/{{ addSelectedConfirmed.total }}），发布会一并计入
            </div>
          </template>
        </div>

        <el-form label-width="100px" size="small">
          <el-form-item label="新版本号">
            <el-input-number v-model="publishDialog.version" :min="1" />
            <span class="publish-hint">建议新版本号 v{{ nextVersion }}</span>
          </el-form-item>
          <el-form-item label="数据划分">
            <el-radio-group v-model="publishDialog.splitMode">
              <el-radio label="all_train">全部训练集</el-radio>
              <el-radio label="all_val">全部验证集</el-radio>
              <el-radio label="all_test">全部测试集</el-radio>
              <el-radio label="ratio">按比例</el-radio>
            </el-radio-group>
            <div v-if="publishDialog.splitMode === 'ratio'" class="split-ratios">
              <span class="split-ratio-item">训练集
                <el-input-number v-model="publishDialog.trainPct" :min="0" :max="100" :precision="0" size="mini" controls-position="right" />
                %
              </span>
              <span class="split-ratio-item">验证集
                <el-input-number v-model="publishDialog.valPct" :min="0" :max="100" :precision="0" size="mini" controls-position="right" />
                %
              </span>
              <span class="split-ratio-item">测试集
                <el-input-number v-model="publishDialog.testPct" :min="0" :max="100" :precision="0" size="mini" controls-position="right" />
                %
              </span>
              <span class="split-ratio-sum" :class="{ bad: ratioTotal !== 100 }">合计 {{ ratioTotal }}%</span>
            </div>
            <span v-if="publishDialog.splitMode === 'ratio'" class="publish-hint">只作用于本次加入的图，三项合计需为 100%</span>
            <span v-if="publishDialog.splitMode === 'all_test'" class="publish-hint">测试划分不参与训练，只用于评估</span>
          </el-form-item>
        </el-form>
        <el-alert
          type="info"
          :closable="false"
          show-icon
          title="加入/移除只影响新版本，历史版本快照不变；移除后可再加回"
        />
      </template>
      <div slot="footer">
        <template v-if="publishDialog.job">
          <el-button v-if="publishJobStatus === 'completed'" type="primary" size="small" @click="finishPublish">完成</el-button>
          <el-button size="small" @click="publishDialog.visible = false">{{ publishInFlight ? '关闭（后台继续）' : '关闭' }}</el-button>
        </template>
        <template v-else>
          <el-button size="small" :disabled="publishDialog.addSelected.length === 0" @click="openPreview">图片预览</el-button>
          <el-button size="small" @click="publishDialog.visible = false">取消</el-button>
          <el-button
            type="primary"
            size="small"
            :loading="publishDialog.submitting"
            :disabled="overlapBatches.length > 0"
            @click="submitPublish"
          >发布 v{{ publishDialog.version }}</el-button>
        </template>
      </div>
    </el-dialog>

    <!-- 类别确认：发布前看 class_map / classes.txt / data.yaml，确认后带 hash 发布 -->
    <el-dialog
      title="类别确认"
      :visible.sync="classPlanDialog.visible"
      width="980px"
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="!classPlanDialog.submitting"
      @close="resetClassPlan"
    >
      <div v-loading="classPlanDialog.loading">
        <template v-if="classPlanDialog.plan">
          <div class="class-plan-head">
            <span>v{{ classPlanDialog.plan.version }} · {{ classPlanDialog.plan.classes.length }} 类</span>
            <span class="muted">{{ classPlanDialog.plan.batches.length }} 批加入 / {{ classPlanDialog.plan.remove_batches.length }} 批移除</span>
          </div>
          <el-alert
            v-for="w in classPlanDialog.plan.warnings || []"
            :key="w"
            type="warning"
            show-icon
            :closable="false"
            :title="w"
            class="class-plan-warn"
          />
          <el-table :data="classPlanDialog.plan.class_map" size="mini" max-height="280" border>
            <el-table-column prop="name" label="本版类名" min-width="160" />
            <el-table-column prop="yolo_id" label="yolo_id" width="80" align="center" />
            <el-table-column label="旧类名/别名" min-width="180">
              <template slot-scope="{ row }">{{ (row.aliases || []).join('、') || '-' }}</template>
            </el-table-column>
            <el-table-column prop="frame_count" label="帧数" width="96" align="right" />
            <el-table-column prop="instance_count" label="实例" width="96" align="right" />
          </el-table>
          <el-alert
            v-if="(classPlanDialog.plan.dropped || []).length"
            type="error"
            show-icon
            :closable="false"
            class="class-plan-warn"
            title="以下类别的标注不会进入本版"
          >
            <div v-for="d in classPlanDialog.plan.dropped" :key="d.name">
              {{ d.name }}：{{ d.frame_count }} 帧 / {{ d.instance_count }} 实例
            </div>
          </el-alert>
          <div class="class-plan-files">
            <div>
              <h4>classes.txt</h4>
              <pre>{{ classPlanDialog.plan.classes_txt }}</pre>
            </div>
            <div>
              <h4>data.yaml</h4>
              <pre>{{ classPlanDialog.plan.data_yaml }}</pre>
            </div>
          </div>
        </template>
      </div>
      <div slot="footer">
        <el-button size="small" @click="classPlanDialog.visible = false">取消</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="classPlanDialog.submitting"
          :disabled="!classPlanDialog.plan"
          @click="confirmClassPlan"
        >确认并发布 v{{ classPlanDialog.plan ? classPlanDialog.plan.version : '' }}</el-button>
      </div>
    </el-dialog>

    <!-- 图片预览：勾选批次分组墙，每组按时间均匀取 8 张代表图 -->
    <el-dialog title="图片预览" :visible.sync="preview.visible" width="1100px" append-to-body>
      <div class="pv-stats">
        <span><b>{{ publishDialog.addSelected.length }}</b> 批 · <b>{{ addSelectedCount }}</b> 张</span>
        <span>目标实例：<b>{{ instanceSummaryText }}</b></span>
      </div>
      <div class="pv-groups" v-loading="false">
        <div v-for="g in preview.groups" :key="g.batch" class="pv-group">
          <div class="pv-group-head" @click="toggleGroup(g)">
            <span class="pv-batch">{{ g.batch }}</span>
            <span class="pv-meta">{{ g.meta }}</span>
            <span class="pv-expand">{{ g.expanded ? '收起 ▴' : `展开全部 ${g.total} 张 ▾` }}</span>
          </div>
          <div class="pv-grid" v-loading="g.loading">
            <div v-for="row in g.images" :key="row.id" class="pv-thumb-wrap">
              <img :src="thumbUrl(row)" :title="row.key" class="pv-thumb" loading="lazy">
              <span v-if="!row.labels" class="pv-unlabeled">无标注</span>
            </div>
            <div v-if="g.images.length === 0 && !g.loading" class="pv-empty">{{ g.error || '该批暂无图片' }}</div>
          </div>
          <div v-if="g.expanded" class="pv-page">
            <el-button size="mini" :disabled="g.page <= 1" @click="changeGroupPage(g, g.page - 1)">‹ 上一页</el-button>
            <span>{{ g.page }} / {{ Math.max(1, Math.ceil(g.total / 40)) }}</span>
            <el-button size="mini" :disabled="g.page >= Math.ceil(g.total / 40)" @click="changeGroupPage(g, g.page + 1)">下一页 ›</el-button>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button size="small" @click="preview.visible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!--
      新建数据集与数据登记合成一个弹窗：先建空数据集，随即在同一弹窗里把批次登记进去。
      「登记批次」入口进来直接停在向导那一步，并锁住当前选中的数据集。
    -->
    <el-dialog
      :title="datasetDialog.stage === 'wizard' ? `登记批次${datasetDialog.datasetLabel ? ` — ${datasetDialog.datasetLabel}` : ''}` : '新建数据集'"
      :visible.sync="datasetDialog.visible"
      :width="datasetDialog.stage === 'wizard' ? '1000px' : '480px'"
      top="5vh"
      :close-on-click-modal="false"
    >
      <el-form v-if="datasetDialog.stage === 'new'" label-width="90px" size="small">
        <el-form-item label="标识">
          <el-input v-model="datasetDialog.slug" placeholder="如 pallet_v2" maxlength="64" />
          <div class="publish-hint">小写字母、数字、下划线、连字符，建了不能改（训练引用与训练机目录都认它）</div>
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="datasetDialog.name" placeholder="可留空，界面回退显示标识" maxlength="256" />
        </el-form-item>
      </el-form>
      <register-wizard
        v-else
        :preset-dataset-id="datasetDialog.datasetId"
        @close="datasetDialog.visible = false"
        @registered="loadBatches"
      />
      <div v-if="datasetDialog.stage === 'new'" slot="footer">
        <el-button size="small" @click="datasetDialog.visible = false">取消</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="datasetDialog.submitting"
          :disabled="!datasetDialog.slug.trim()"
          @click="submitDataset"
        >创建并登记批次</el-button>
      </div>
    </el-dialog>

    <!-- 类别管理：注册表与项目标签同步都在这；整理数据集时先把标签定好，再登记批次 -->
    <el-dialog
      title="类别管理"
      :visible.sync="classDialog.visible"
      width="1040px"
      top="6vh"
      append-to-body
    >
      <classes v-if="classDialog.visible" />
    </el-dialog>

    <!-- 批次记录：这个数据集登记过哪些批次、各自的流水线状态；预标注是否派发/起止/写回 LS 也在这一行里 -->
    <el-dialog
      title="批次记录"
      :visible.sync="batchLog.visible"
      width="1100px"
      top="6vh"
    >
      <div class="batch-log-head">
        <span class="muted">共 {{ batches.length }} 个批次（按登记时间倒序）</span>
        <el-button size="mini" icon="el-icon-refresh" :loading="batchLog.loading" @click="loadBatchLog">刷新</el-button>
      </div>
      <el-table
        v-loading="batchLog.loading"
        class="batch-log-table"
        :data="batches"
        stripe
        border
        size="small"
        style="width:100%"
      >
        <!-- 列宽固定 px（弹窗 1100 内容约 1060）：长文本单行省略＋悬停看全文；末列不设宽自动吃剩余，保证不出横向滚动 -->
        <el-table-column label="批次" width="155" show-overflow-tooltip>
          <template slot-scope="{ row }"><span class="mono">{{ row.batch }}</span></template>
        </el-table-column>
        <el-table-column label="图片数" width="65" align="center">
          <template slot-scope="{ row }">{{ row.image_count }}</template>
        </el-table-column>
        <el-table-column label="流水线状态" width="95" align="center">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="stageTag(row.stage)">{{ stageLabel(row.stage) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="预标注" width="250" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <template v-if="row.preannotate_job">
              <span>{{ preannotateText(row.preannotate_job) }}</span>
              <span v-if="receiptOf(row)" class="batch-log-note"> · {{ receiptOf(row) }}</span>
            </template>
            <span v-else class="muted">未登记预标注</span>
          </template>
        </el-table-column>
        <el-table-column label="标注" width="190" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span>已人工标注 {{ row.annotated_reviewed }}/{{ row.image_count }}</span>
            <span v-if="row.annotated_empty" class="batch-log-note"> · 未发现目标 {{ row.annotated_empty }}</span>
          </template>
        </el-table-column>
        <el-table-column label="版本" width="110" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span v-if="row.intervals && row.intervals.length">{{ intervalText(row.intervals) }}</span>
            <span v-else class="muted">未加入版本</span>
          </template>
        </el-table-column>
        <el-table-column label="标注项目" width="90" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <a
              v-if="row.ls_project_url"
              class="ls-link"
              :href="row.ls_project_url"
              target="_blank"
              rel="noopener"
            >{{ (row.job && row.job.project_name) || row.batch }}</a>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="登记时间" align="center">
          <template slot-scope="{ row }">
            <span :title="formatTime(row.created_at)">{{ shortTime(row.created_at) }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button size="small" @click="batchLog.visible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 版本清单 / 本地缓存下载的进度弹窗 -->
    <zip-download-dialog ref="zipDl" title="版本下载" />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { fetchTrainDatasets, materializeDataset } from '../../api/blissrose/training';
import { deleteCache, fetchCacheInventory } from '../../api/blissrose/dataset';
import {
  fetchDatasetVersions, fetchDatasetImages, fetchDatasetImagesStats, publishDatasetVersion,
  previewDatasetClassPlan,
  fetchLatestPublishJob, fetchDatasetJob,
  fetchDatasetVersionDownloadManifest, fetchDatasets, createDataset,
  hideDatasetVersion,
} from '../../api/dataset';
import {
  dominantOf, matchesTagFilters, matchesLabelFilter, uniformPick, uniformSample, sumLabelStats, topLabels,
} from '../../utils/publish';
import { PIPELINE_STATES } from '../../utils/labels';
import { preAnnotateStatusText, preAnnotateReceiptText } from '../../utils/preannotate';
import ZipDownloadDialog from '../../components/ZipDownloadDialog.vue';
import RegisterWizard from './RegisterWizard.vue';
import Classes from './Classes.vue';

/** 下载到训练机的轮询节奏：落地是后台拉对象，2 秒问一次够用 */
const PULL_POLL_MS = 2000;
/** 连续几轮问不通就停（问不通不提示，等用户自己刷新） */
const PULL_POLL_MAX_FAILURES = 3;
/** 发布任务轮询节奏：后端锁轮 10 秒取一次任务，3 秒问一次够细 */
const PUBLISH_POLL_MS = 1000;
/** 连续几轮问不通就停询，进度视图给「重新查询」的恢复出口 */
const PUBLISH_POLL_MAX_FAILURES = 5;

const CACHE_META = {
  ready: { label: '已就绪', tag: 'success' },
  pulling: { label: '下载中', tag: 'warning' },
  missing: { label: '未下载', tag: 'info' },
  stale: { label: '需重新下载', tag: 'warning' },
};

/** 按 1024 进制把字节数说成看得懂的量级；取不到时显示 '-' */
function formatBytes(bytes) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === null || bytes === undefined || bytes === '') return '-';
  const value = Number(bytes);
  if (!Number.isFinite(value)) return '-';
  let scaled = value;
  let index = 0;
  while (scaled >= 1024 && index < units.length - 1) {
    scaled /= 1024;
    index += 1;
  }
  return index === 0 ? `${scaled} B` : `${scaled.toFixed(1)} ${units[index]}`;
}

/** 显示用分组名：去掉结尾版本号（-v78）与 -all；只影响显示，判定按整个名字算族 */
function familyOf(name) {
  const text = String(name || '');
  return text.replace(/[-_.]v\d+$/i, '').replace(/-all$/i, '') || text;
}

export default {
  name: 'BlissroseDatasets',

  components: { ZipDownloadDialog, RegisterWizard, Classes },

  data() {
    return {
      keyword: '',
      datasets: [], // 数据集名册（选择器）
      datasetId: null, // 当前数据集；null＝还没读到名册，读写都回退默认数据集
      // 新建数据集 / 登记批次 合用的弹窗：stage＝new 先填标识，wizard＝登记向导
      datasetDialog: {
        visible: false, submitting: false, stage: 'new', slug: '', name: '',
        datasetId: null, datasetLabel: '',
      },
      classDialog: { visible: false },
      // 批次记录弹窗：直接渲染 loadBatches() 的候选池行（同一份聚合，不再单独取数）
      batchLog: { visible: false, loading: false },
      loadingVersions: false,
      versions: [],
      platformVersions: [], // 发布/训练使用/删除的来源
      batches: [], // 发布弹窗的批次候选
      batchByName: {}, // batch → 批次聚合
      publishDialog: {
        visible: false,
        addProject: '', // 加入栏：按标注项目筛
        addStatus: '', // 加入栏：'' 全部 | unpublished 未发布 | removed 曾剔除
        removeProject: '', // 剔除栏：按标注项目筛
        removeVersion: null, // 剔除栏：按所属版本筛
        addSelected: [],
        removeSelected: [],
        version: null,
        splitMode: 'all_train',
        trainPct: 70,
        valPct: 20,
        testPct: 10,
        submitting: false,
        // 条件筛选：标签组各选一个值（空 = 不限）+ 标注条件 + 采样上限
        cond: {
          scene: '', weather: '', time: '', lighting: '',
          label: '', minInstances: 1, limitBatches: false, sampleN: 5,
        },
        condCollapsed: false,
        job: null, // 在途/刚结束的发布任务：非空时弹窗切进度视图
      },
      preview: { visible: false, groups: [] }, // 图片预览：勾选批次分组墙
      classPlanDialog: { visible: false, loading: false, submitting: false, plan: null, payload: null, seq: 0 },
      loadingInventory: false,
      inventory: null,
      selected: [],
      reclaiming: false,
      result: null,
      pullingRefs: [], // 正在下载到训练机的版本（轮询名单）
      postingRef: null, // 下载请求在途的版本（按钮转圈）
      pollTimer: null,
      pollFailures: 0,
      publishTimer: null, // 发布进度轮询定时器
      publishFailures: 0, // 发布进度连续问不通的轮数
      publishPollDead: false, // 连败停询：进度视图显示「重新查询」
    };
  },

  beforeDestroy() {
    this.stopPolling();
    this.stopPublishPolling();
  },

  computed: {
    /** 本地筛选：数字版本在前（降序），日期式版本号在后 */
    filteredVersions() {
      const kw = this.keyword.trim().toLowerCase();
      const rows = kw
        ? this.versions.filter(row => {
          const fields = [row.name, row.version, row.ref];
          return fields.filter(Boolean).some(v => String(v).toLowerCase().includes(kw));
        })
        : this.versions;
      return [...rows].sort((a, b) => {
        const an = Number(a.version);
        const bn = Number(b.version);
        const aNum = Number.isFinite(an);
        const bNum = Number.isFinite(bn);
        if (aNum && bNum) return bn - an;
        if (aNum !== bNum) return aNum ? -1 : 1;
        return String(b.version).localeCompare(String(a.version));
      });
    },

    /** 本地盘上的版本，按引用索引 */
    localByRef() {
      const index = {};
      for (const row of (this.inventory || {}).versions || []) index[row.ref] = row;
      return index;
    },

    /** 块二的表：版本与权重合成一张，一次勾选、一次确认 */
    rows() {
      const inv = this.inventory || {};
      const versions = (inv.versions || []).map(row => ({
        key: `v:${row.ref}`,
        kind: 'version',
        ref: row.ref,
        label: row.ref,
        sub: row.state === 'ready' ? `就绪于 ${this.formatTime(row.finished_at)}` : '未完成',
        bytes: row.bytes,
        free: row.free_bytes,
        verdict: row.verdict,
        reasons: row.reasons || [],
        last_dispatched_at: row.last_dispatched_at,
        // 旧格式目录（版本号是日期串）：自成一族，自动回收永不覆盖
        legacy: Boolean(row.legacy),
        forceable: Boolean(row.forceable),
      }));
      const models = (inv.models || []).map(row => ({
        key: `m:${row.model_id}`,
        kind: 'model',
        model_id: row.model_id,
        label: row.model_id,
        sub: row.model_name || (row.has_weight ? '' : '无权重文件'),
        bytes: row.bytes,
        free: row.bytes,
        verdict: row.verdict,
        reasons: row.reasons || [],
        last_dispatched_at: row.last_dispatched_at,
      }));
      return [...versions, ...models];
    },

    totals() {
      return (this.inventory || {}).totals || {};
    },

    pool() {
      return (this.inventory || {}).pool || {};
    },

    pulling() {
      return (this.inventory || {}).pulling || [];
    },

    strays() {
      return (this.inventory || {}).strays || [];
    },

    rootFree() {
      return ((this.inventory || {}).root || {}).free_bytes;
    },

    /** 事实不是 ok 就把原因显示出来 */
    factsError() {
      const facts = (this.inventory || {}).facts;
      if (!facts || facts.state === 'ok') return '';
      return facts.error || '训练机未给出原因';
    },

    /** 正在发布的版本号；存在时禁止再发起发布（后端同样会拒绝） */
    publishingVersion() {
      const row = (this.platformVersions || []).find(v => v.status === 'publishing');
      return row ? row.version : null;
    },

    /** 下一个建议版本号：这个数据集已发布版本的最大值 + 1 */
    nextVersion() {
      const max = this.platformVersions.reduce((m, v) => Math.max(m, v.version), 0);
      return max + 1;
    },

    /** 发布任务还在途（排队或执行中） */
    publishInFlight() {
      const job = this.publishDialog.job;
      return !!job && (job.status === 'pending' || job.status === 'processing');
    },

    publishJobStatus() {
      const job = this.publishDialog.job;
      return job ? job.status : null;
    },

    /** 完成文案：与同步发布时代的提示一致，结果从 job.options.result 取 */
    publishResultText() {
      const job = this.publishDialog.job;
      if (!job) return '';
      const r = (job.options && job.options.result) || null;
      if (!r) return `已发布 v${job.version}`;
      const parts = [];
      if (r.added > 0) parts.push(`加入 ${r.added} 张（train ${r.train} / val ${r.val}）`);
      if (r.removed > 0) parts.push(`移除 ${r.removed} 张`);
      const unconfirmed = Number((r.unconfirmed || {}).images) || 0;
      if (unconfirmed > 0) parts.push(`其中 ${unconfirmed} 张未人工标注`);
      return `已发布 v${r.version}：${parts.join('，')}`;
    },

    /** 三比例合计（用于校验和提示） */
    ratioTotal() {
      return (Number(this.publishDialog.trainPct) || 0)
        + (Number(this.publishDialog.valPct) || 0)
        + (Number(this.publishDialog.testPct) || 0);
    },

    /** 当前数据：最新的已发布版本 */
    currentVersion() {
      const list = [...this.platformVersions].sort((a, b) => b.version - a.version);
      return list.find(v => v.frames > 0) || null;
    },

    currentFrames() {
      return (this.currentVersion && this.currentVersion.frames) || 0;
    },

    /** 加入池：不在当前数据的批次（未发布 + 曾剔除） */
    addPool() {
      return this.batches.filter(b => !this.isLive(b));
    },

    /** 剔除池：在当前数据的批次 */
    removePool() {
      return this.batches.filter(b => this.isLive(b));
    },

    addProjectOptions() {
      return [...new Set(this.addPool.map(b => b.job && b.job.project_name).filter(Boolean))];
    },

    removeProjectOptions() {
      return [...new Set(this.removePool.map(b => b.job && b.job.project_name).filter(Boolean))];
    },

    removeVersionOptions() {
      return [...new Set(this.removePool.map(b => b.added_in_version).filter(v => v != null))]
        .sort((a, b) => b - a);
    },

    addCandidates() {
      const p = this.publishDialog.addProject;
      const s = this.publishDialog.addStatus;
      return this.addPool.filter(b => {
        if (p && (b.job && b.job.project_name) !== p) return false;
        if (s === 'unpublished' && b.added_in_version != null) return false;
        if (s === 'removed' && b.removed_in_version == null) return false;
        return true;
      });
    },

    /** 条件筛选：非空的标签组条件（每组至多选一个） */
    condTagFilters() {
      const c = this.publishDialog.cond;
      return ['scene', 'weather', 'time', 'lighting']
        .filter(g => c[g])
        .map(g => ({ group: g, value: c[g] }));
    },

    condActive() {
      return this.condTagFilters.length > 0 || !!this.publishDialog.cond.label;
    },

    /** 符合条件的批次：标签主导值 + 标注实例数阈值；标签未知不命中标签条件；与项目/状态筛选是 AND */
    condMatched() {
      const c = this.publishDialog.cond;
      return this.addCandidates.filter(b =>
        matchesTagFilters(b.tags, this.condTagFilters) &&
        matchesLabelFilter(b.label_stats, { label: c.label, minInstances: c.minInstances }));
    },

    /** 采样后的符合条件的批次（勾了「限制批数」才按时间均匀取 N）；条件全空返回 null = 不干预勾选 */
    sampledMatched() {
      if (!this.condActive) return null;
      const c = this.publishDialog.cond;
      return uniformSample(this.condMatched, c.limitBatches ? c.sampleN : null);
    },

    /** 某标签组在候选池里出现过的值（下拉选项） */
    tagGroupOptions() {
      const options = {};
      for (const g of ['scene', 'weather', 'time', 'lighting']) {
        const values = new Set();
        for (const b of this.addPool) {
          const dist = b.tags && b.tags[g];
          if (dist && typeof dist === 'object') Object.keys(dist).forEach(v => values.add(v));
        }
        options[g] = [...values];
      }
      return options;
    },

    /** 标注条件类别选项：候选池 label_stats 里出现过的类别 */
    labelOptions() {
      const values = new Set();
      for (const b of this.addPool) {
        Object.keys(b.label_stats || {}).forEach(v => values.add(v));
      }
      return [...values];
    },

    /** 勾选批次的实例汇总（结果预览第二行） */
    addInstanceSummary() {
      return sumLabelStats(this.publishDialog.addSelected.map(name => this.batchByName[name] || {}));
    },

    instanceSummaryText() {
      const top = topLabels(this.addInstanceSummary, 3);
      return top.map(([label, n]) => `${label} ${n}`).join(' · ') || '-';
    },

    removeCandidates() {
      const p = this.publishDialog.removeProject;
      const v = this.publishDialog.removeVersion;
      return this.removePool.filter(b => {
        if (p && (b.job && b.job.project_name) !== p) return false;
        if (v != null && b.added_in_version !== v) return false;
        return true;
      });
    },

    addSelectedCount() {
      return this.publishDialog.addSelected
        .reduce((sum, name) => sum + (Number((this.batchByName[name] || {}).image_count) || 0), 0);
    },

    /** 勾选批次的已确认/未确认合计（发布前提示用） */
    addSelectedConfirmed() {
      let reviewed = 0;
      let total = 0;
      for (const name of this.publishDialog.addSelected) {
        const b = this.batchByName[name] || {};
        reviewed += Number(b.annotated_reviewed) || 0;
        total += Number(b.image_count) || 0;
      }
      return { reviewed, total, missing: Math.max(0, total - reviewed) };
    },

    removeSelectedCount() {      return this.publishDialog.removeSelected
        .reduce((sum, name) => sum + (Number((this.batchByName[name] || {}).image_count) || 0), 0);
    },

    previewFrames() {
      return this.currentFrames + this.addSelectedCount - this.removeSelectedCount;
    },

    overlapBatches() {
      return this.publishDialog.addSelected.filter(b => this.publishDialog.removeSelected.includes(b));
    },

    selectedCount() {
      return this.selected.length;
    },

    reclaimLabel() {
      return this.selectedCount ? `回收选中（${this.selectedCount}）` : '回收选中';
    },

    /** 预计释放量：含一并清理的 sweepable 实体；实际数字以服务端 freed_bytes 为准 */
    expectedBytes() {
      let total = Number(this.pool.sweepable_bytes) || 0;
      for (const row of this.selected) {
        total += Number(row.kind === 'version' ? row.free : row.bytes) || 0;
      }
      return total;
    },

    resultTitle() {
      const result = this.result || {};
      const parts = [`已释放 ${formatBytes(result.freed_bytes)}`];
      parts.push(`删除 ${(result.deleted || []).length} 项`);
      if ((result.skipped || []).length) parts.push(`跳过 ${result.skipped.length} 项`);
      if (result.pending_bytes) parts.push(`另有 ${formatBytes(result.pending_bytes)} 未清理`);
      return parts.join(' · ');
    },
  },

  watch: {
    // 条件变化即重算勾选；条件全空不干预
    'publishDialog.cond': {
      deep: true,
      handler() {
        if (!this.condActive || !this.sampledMatched) return;
        this.publishDialog.addSelected = this.sampledMatched.map(b => b.batch);
      },
    },
  },

  async mounted() {
    // 名册先到，后面两次拉取才带得上数据集维
    await this.loadDatasets();
    // 两块各取各的：扫描扫盘、清单读 MinIO；不轮询
    this.loadAll();
    this.loadBatches();
  },

  methods: {
    /** 数据集名册：默认选中配置里的默认数据集（不选时后端也按它走，两边一致） */
    async loadDatasets(preferId = null) {
      try {
        const data = (await fetchDatasets()).data || {};
        this.datasets = data.items || [];
        const wanted = preferId
          || (this.datasets.find(d => d.slug === data.default_slug) || {}).id
          || null;
        if (wanted) this.datasetId = wanted;
      } catch (err) {
        // 名册拿不到不挡页面：读写都回退后端默认数据集
      }
    },

    /** 切数据集：版本、批次、发布候选全按新数据集重取（批次记录弹窗开着就跟着换数据） */
    async onDatasetChange() {
      this.keyword = '';
      await Promise.all([this.loadVersions(), this.batchLog.visible ? this.loadBatchLog() : this.loadBatches()]);
    },

    openDatasetDialog() {
      Object.assign(this.datasetDialog, {
        visible: true, stage: 'new', slug: '', name: '', submitting: false,
        datasetId: null, datasetLabel: '',
      });
    },

    /** 登记批次：不进「新建」那步，带着当前数据集直接开向导（没选＝默认数据集） */
    openRegisterDialog() {
      const current = this.datasets.find(d => d.id === this.datasetId);
      Object.assign(this.datasetDialog, {
        visible: true, stage: 'wizard', slug: '', name: '', submitting: false,
        datasetId: this.datasetId,
        datasetLabel: current ? (current.name ? `${current.name}（${current.slug}）` : current.slug) : '',
      });
    },

    openClassDialog() {
      this.classDialog.visible = true;
    },

    /** 建空数据集：建成不关弹窗，在同一弹窗里接着登记批次 */
    async submitDataset() {
      const d = this.datasetDialog;
      if (!d.slug.trim()) {
        this.$message.warning('请填写数据集标识');
        return;
      }
      d.submitting = true;
      try {
        const row = (await createDataset({ slug: d.slug.trim(), name: d.name.trim() || undefined })).data;
        this.$message.success(`已创建数据集 ${row.slug}`);
        d.datasetId = row.id;
        d.datasetLabel = row.name ? `${row.name}（${row.slug}）` : row.slug;
        d.stage = 'wizard';
        await this.loadDatasets(row.id);
        await this.onDatasetChange();
      } catch (err) {
        // 错误已由拦截器统一提示（重名回 409）
      } finally {
        d.submitting = false;
      }
    },

    /** 批次是否在当前数据（added 有值且未剔除） */
    isLive(b) {
      return b.added_in_version != null && b.removed_in_version == null;
    },

    async loadAll() {
      await Promise.all([this.loadVersions(), this.loadInventory()]);
    },

    /** 打开发布弹窗：先刷记录和批次；在途发布直接进进度视图，上次失败给黄条 */
    async openPublish() {
      if (this.publishingVersion) {
        this.$message.warning(`v${this.publishingVersion} 正在发布，完成后再试`);
        return;
      }
      await Promise.all([this.loadBatches(), this.loadVersions()]);
      const d = this.publishDialog;
      d.addProject = '';
      d.addStatus = '';
      d.removeProject = '';
      d.removeVersion = null;
      d.addSelected = [];
      d.removeSelected = [];
      d.cond = { scene: '', weather: '', time: '', lighting: '', label: '', minInstances: 1, limitBatches: false, sampleN: 5 };
      d.version = this.nextVersion;
      d.splitMode = 'all_train';
      d.trainPct = 70;
      d.valPct = 20;
      d.testPct = 10;
      d.job = null;
      this.publishPollDead = false;
      this.stopPublishPolling();
      try {
        const latest = (await fetchLatestPublishJob(this.datasetId)).data;
        if (latest && (latest.status === 'pending' || latest.status === 'processing')) {
          // 关弹窗/刷新页面后回来：直接接着看进度
          d.job = latest;
          this.startPublishPolling();
        }
        // completed / failed 不打断：列表就是当前状态
      } catch (err) {
        // 找回失败不挡主流程，用户仍可照常发起新发布
      }
      d.visible = true;
    },

    async submitPublish() {
      const d = this.publishDialog;
      if (this.overlapBatches.length > 0) {
        this.$message.warning(`批次 ${this.overlapBatches.join('、')} 不能同时加入和移除`);
        return;
      }
      if (d.addSelected.length === 0 && d.removeSelected.length === 0) {
        this.$message.warning('请选择要加入或要移除的批次');
        return;
      }
      if (!d.version || d.version <= 0) {
        this.$message.warning('请填写新版本号');
        return;
      }
      if (this.previewFrames <= 0) {
        this.$message.warning('发布后的版本不能是 0 张或更少，请调整加入与移除的批次');
        return;
      }
      // 未确认标注不拦截发布，但要让用户明确知道有多少张只有预标注结果
      if (this.addSelectedConfirmed.missing > 0) {
        try {
          await this.$confirm(
            `本次加入的 ${d.addSelected.length} 个批次里有 ${this.addSelectedConfirmed.missing} 张未人工标注`
            + `（已人工标注 ${this.addSelectedConfirmed.reviewed}/${this.addSelectedConfirmed.total}），`
            + '继续发布会把这些图一并计入版本。',
            '存在未人工标注',
            { type: 'warning', confirmButtonText: '仍然发布', cancelButtonText: '取消' },
          );
        } catch (err) {
          return; // 用户取消
        }
      }
      if (d.splitMode === 'ratio' && this.ratioTotal !== 100) {
        this.$message.warning('训练集、验证集、测试集比例合计必须为 100%');
        return;
      }
      const payload = {
        version: d.version,
        batches: d.addSelected,
        remove_batches: d.removeSelected,
        dataset_id: this.datasetId,
        split_rule: {
          split: d.splitMode,
          ...(d.splitMode === 'ratio' ? {
            train_ratio: Number(d.trainPct) / 100,
            val_ratio: Number(d.valPct) / 100,
            test_ratio: Number(d.testPct) / 100,
          } : {}),
          seed: 42,
        },
      };
      // 先预览类别（class_map / classes.txt / data.yaml），人工确认后再发布
      await this.previewClassPlanBeforePublish(payload);
    },

    /** 打开类别确认弹窗；确认后由 confirmClassPlan 带 hash 发布 */
    async previewClassPlanBeforePublish(payload) {
      const cp = this.classPlanDialog;
      const seq = ++cp.seq;
      cp.payload = payload;
      cp.plan = null;
      cp.visible = true;
      cp.loading = true;
      try {
        const res = await previewDatasetClassPlan(payload);
        if (seq !== cp.seq) return; // 已经有更新的预览请求
        cp.plan = res.data;
      } catch (err) {
        if (seq === cp.seq) cp.visible = false;
      } finally {
        if (seq === cp.seq) cp.loading = false;
      }
    },

    /** 类别确认：带 class_plan_hash 发布；hash 过期会 409，留在弹窗重新预览 */
    async confirmClassPlan() {
      const cp = this.classPlanDialog;
      if (!cp.plan || !cp.payload) return;
      cp.submitting = true;
      try {
        await this._publishConfirmed({ ...cp.payload, class_plan_hash: cp.plan.class_plan_hash });
      } catch (err) {
        if (err && err.response && err.response.status === 409) {
          // 类别表在预览后变过：丢掉旧 hash，重新预览
          await this.previewClassPlanBeforePublish(cp.payload);
        }
      } finally {
        cp.submitting = false;
      }
    },

    /** 发布一条已确认的 payload；成功切进度视图，失败抛给调用方 */
    async _publishConfirmed(payload) {
      const d = this.publishDialog;
      d.submitting = true;
      try {
        const res = await publishDatasetVersion(payload);
        d.job = { id: res.data.job_id, version: res.data.version, status: 'pending', options: payload };
        this.classPlanDialog.visible = false;
        this.startPublishPolling();
      } finally {
        d.submitting = false;
      }
    },

    /** 类别弹窗关闭：清掉旧 plan/payload，避免下次打开闪旧数据 */
    resetClassPlan() {
      const cp = this.classPlanDialog;
      cp.seq += 1;
      cp.loading = false;
      cp.submitting = false;
      cp.plan = null;
      cp.payload = null;
    },



    /** 完成：关闭弹窗（版本列表/批次在完成时已刷新） */
    finishPublish() {
      this.stopPublishPolling();
      this.publishDialog.visible = false;
    },

    /** 弹窗关闭（X/取消/完成后）：停轮询；在途任务继续在后台跑，重开时由 publish/latest 接回 */
    onPublishDialogClose() {
      this.stopPublishPolling();
    },

    /** 发布进度轮询：3 秒问一次任务状态，完成/失败即停 */
    startPublishPolling() {
      this.stopPublishPolling();
      this.publishPollDead = false;
      this.publishFailures = 0;
      this.publishTimer = setInterval(this.pollPublishOnce, PUBLISH_POLL_MS);
      this.pollPublishOnce(); // 立刻查一次，别等第一个 3 秒
    },

    stopPublishPolling() {
      if (this.publishTimer) {
        clearInterval(this.publishTimer);
        this.publishTimer = null;
      }
      this.publishFailures = 0;
    },

    async pollPublishOnce() {
      const d = this.publishDialog;
      if (!d.job || !d.job.id) return;
      let job;
      try {
        job = (await fetchDatasetJob(d.job.id)).data;
        this.publishFailures = 0;
        this.publishPollDead = false;
      } catch (err) {
        this.publishFailures += 1;
        if (this.publishFailures >= PUBLISH_POLL_MAX_FAILURES) {
          this.publishPollDead = true;
          this.stopPublishPolling();
        }
        return;
      }
      if (!job) return;
      d.job = job;
      if (job.status === 'completed') {
        this.stopPublishPolling();
        await Promise.all([this.loadVersions(), this.loadBatches()]);
      } else if (job.status === 'failed') {
        this.stopPublishPolling(); // 停在失败视图：版本已回滚，等用户关闭后重新发布
      }
    },

    /** 「重新查询」：清连败计数并立刻再查一次、重启轮询 */
    requeryPublish() {
      this.publishPollDead = false;
      this.startPublishPolling();
    },

    /** 加入栏的状态标签：未发布 / 剔除于 vN */
    addStatusOf(b) {
      if (b.added_in_version == null) return { text: '未加入版本', type: 'warning' };
      return { text: `已从 v${b.removed_in_version} 移除`, type: 'info' };
    },

    /** 单批未确认张数（image_count − annotated_reviewed，只用于标黄） */
    confirmedGapOf(b) {
      return Math.max(0, (Number(b.image_count) || 0) - (Number(b.annotated_reviewed) || 0));
    },

    /** 批次行标签列：场景/天气的主导值 + 百分比 */
    topTagPairs(b) {
      if (!b.tags || typeof b.tags !== 'object' || Object.keys(b.tags).length === 0) return [];
      const out = [];
      for (const g of ['scene', 'weather']) {
        const dist = b.tags[g];
        if (!dist || typeof dist !== 'object') continue;
        const v = dominantOf(b.tags, g);
        if (v != null) out.push([v, Math.round((Number(dist[v]) || 0) * 100)]);
      }
      return out;
    },

    /** 批次行实例列：top 2 类别 */
    instanceText(b) {
      const top = topLabels(b.label_stats, 2);
      return top.map(([label, n]) => `${label} ${n}`).join(' · ') || '-';
    },

    clearCond() {
      this.publishDialog.cond = {
        scene: '', weather: '', time: '', lighting: '',
        label: '', minInstances: 1, limitBatches: false, sampleN: 5,
      };
    },

    thumbUrl(row) {
      return `/api/dataset/images/${row.id}/thumb?w=200`;
    },

    /** 打开图片预览：勾选批次分组墙，各组并行加载 */
    async openPreview() {
      this.preview.visible = true;
      this.preview.groups = [];
      await Promise.all(this.publishDialog.addSelected.map(name => this._buildPreviewGroup(name)));
    },

    /** 一组预览：接口按 image_key 排序（相机在前）；超过一页时按 total 分散取页 */
    async _buildPreviewGroup(name) {
      const b = this.batchByName[name];
      if (!b) return;
      const group = {
        batch: name,
        meta: this.previewMeta(b),
        expanded: false,
        total: Number(b.image_count) || 0,
        images: [],
        page: 1,
        loading: true,
        error: '',
      };
      this.preview.groups.push(group);
      try {
        const first = await fetchDatasetImages({ batch: name, dataset_id: this.datasetId, page: 1, page_size: 100 });
        const rows = [...(first.data.rows || [])];
        const pages = Math.ceil((first.data.total || 0) / 100);
        const picks = [...new Set([1, Math.ceil(pages / 3), Math.ceil((2 * pages) / 3), pages])];
        for (const p of picks) {
          if (p === 1) continue;
          const res = await fetchDatasetImages({ batch: name, dataset_id: this.datasetId, page: p, page_size: 100 });
          rows.push(...(res.data.rows || []));
        }
        group.images = uniformPick(rows, 8).map(r => ({
          id: r.id, key: r.image_key, annotate_state: r.annotate_state, labels: r.labels,
        }));
      } catch (err) {
        group.error = (err && err.message) || '代表图加载失败';
      } finally {
        group.loading = false;
      }
    },

    previewMeta(b) {
      const parts = [`${Number(b.image_count) || 0} 张`];
      for (const [v, pct] of this.topTagPairs(b)) parts.push(`${v} ${pct}%`);
      const top = topLabels(b.label_stats, 1);
      if (top.length) parts.push(`${top[0][0]} ${top[0][1]}`);
      return parts.join(' · ');
    },

    async toggleGroup(group) {
      if (group.expanded) {
        group.expanded = false;
        return;
      }
      group.expanded = true;
      group.page = 1;
      await this.loadGroupPage(group);
    },

    async loadGroupPage(group) {
      group.loading = true;
      try {
        const res = await fetchDatasetImages({
          batch: group.batch, dataset_id: this.datasetId, page: group.page, page_size: 40,
        });
        group.images = (res.data.rows || []).map(r => ({
          id: r.id, key: r.image_key, annotate_state: r.annotate_state, labels: r.labels,
        }));
        group.total = res.data.total;
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        group.loading = false;
      }
    },

    changeGroupPage(group, page) {
      group.page = page;
      this.loadGroupPage(group);
    },

    async hideVersion(row) {
      try {
        await this.$confirm(
          `从版本列表移除 v${row.version}？\n只隐藏列表/训练入口，不删除版本、shard、label 或本地缓存。`,
          '隐藏版本',
          { type: 'warning', confirmButtonText: '隐藏', cancelButtonText: '取消' },
        );
      } catch (err) {
        return;
      }
      try {
        await hideDatasetVersion(row.version, this.datasetId);
        this.$message.success(`v${row.version} 已隐藏`);
        await this.loadVersions();
      } catch (err) {
        // 错误由响应拦截器统一提示
      }
    },

    async loadVersions() {
      this.loadingVersions = true;
      try {
        // 两侧各自取：一侧挂了不把整张表打空
        const [side, platform] = await Promise.allSettled([
          fetchTrainDatasets(),
          fetchDatasetVersions(this.datasetId),
        ]);
        const items = side.status === 'fulfilled' ? (side.value.data && side.value.data.items) || [] : [];
        this.platformVersions = platform.status === 'fulfilled' ? platform.value.data || [] : [];
        const merged = items.map(row => {
          const pv = this.matchPlatformVersion(row);
          // 训练侧清单不带批次明细，版本列表的「批次」列用平台版本记录补齐
          return { ...row, pv, batches: (pv && pv.batches) || row.batches || [] };
        });
        const matched = new Set(merged.map(r => r.pv && r.pv.version).filter(Boolean));
        // 平台上有、训练侧清单还没有的版本也列出来
        for (const pv of this.platformVersions) {
          if (matched.has(pv.version)) continue;
          merged.push({
            name: pv.name,
            version: String(pv.version),
            ref: pv.ref,
            frame_count: pv.frames,
            train_count: pv.train,
            val_count: pv.val,
            test_count: pv.test,
            class_count: pv.class_count || 0,
            status: pv.status,
            content_hash: pv.content_hash,
            classes: pv.classes || [],
            batches: pv.batches || [],
            bytes: pv.bytes,
            cache: { state: 'missing' },
            pv,
          });
        }
        this.versions = merged;
      } catch (err) {
        // 提示由响应拦截器统一弹出；保留上一次的清单
      } finally {
        this.loadingVersions = false;
      }
    },

    /** 版本行与数据平台版本记录按版本号对上（两侧都有名字时才比家族名） */
    matchPlatformVersion(row) {
      const v = Number(row.version);
      if (!Number.isFinite(v)) return null;
      return this.platformVersions.find(
        p => p.version === v
          && (!p.name || !row.name || familyOf(p.name) === familyOf(row.name)),
      ) || null;
    },

    // ====== 下载到训练机本地 ======

    /** 版本行的标注项目：带 id 与直达链接（后端按配置拼好地址，没记录时退回纯文本） */
    projectItems(row) {
      return (row.pv && row.pv.project_items) || [];
    },

    isPulling(row) {
      return row.cache_state === 'pulling' || this.pullingRefs.includes(row.ref);
    },

    /** 没下到训练机的版本才给下载入口；请求在途或正在下载时不给 */
    canDownload(row) {
      return Boolean(row.ref) && row.cache_state !== 'ready' && !this.isPulling(row);
    },

    /** 已拉下来多少：总字节未知时不显示百分比 */
    pullPercent(row) {
      const total = Number(row.cache_total) || 0;
      if (!total) return '';
      return `${Math.floor(((Number(row.cache_done) || 0) / total) * 100)}%`;
    },

    /** 把缓存字段贴回版本行（整表替换，避免给行新增属性丢响应式） */
    patchCache(ref, fields) {
      this.versions = this.versions.map(row => (row.ref === ref ? { ...row, ...fields } : row));
    },

    /** 下载该版本到训练机本地：平台立刻返回，落地状态靠轮询训练侧清单 */
    async handleDownload(row) {
      if (!row.ref || this.postingRef) return;
      this.postingRef = row.ref;
      this.startPolling();
      if (!this.pullingRefs.includes(row.ref)) this.pullingRefs.push(row.ref);
      try {
        const { state } = (await materializeDataset(row.ref)).data;
        if (state === 'ready') {
          // 训练机上已经有了：不用进轮询名单
          this.$message.success(`${row.ref} 训练机上已经有了，不必再下载`);
          this.dropPulled(row.ref);
          await this.loadVersions();
          return;
        }
        // 确定在下：当场把标签改成「下载中」，别等下一轮轮询
        this.patchCache(row.ref, { cache_state: 'pulling', cache_error: null });
        this.$message.success(`已开始下载 ${row.ref}，下载完会在这里显示`);
      } catch (err) {
        // 拒绝原因由响应拦截器弹出；该版本移出轮询名单
        this.dropPulled(row.ref);
      } finally {
        this.postingRef = null;
      }
    },

    startPolling() {
      if (this.pollTimer) return;
      this.pollFailures = 0;
      this.pollTimer = setInterval(this.pollOnce, PULL_POLL_MS);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.pollFailures = 0;
    },

    /** 把该版本移出轮询名单：平台响应已当场确认，不用再复核 */
    dropPulled(ref) {
      this.pullingRefs = this.pullingRefs.filter(item => item !== ref);
      if (!this.pullingRefs.length) this.stopPolling();
    },

    /** 一轮轮询：直接问训练侧清单（缓存状态的唯一来源），按引用贴回本页版本行 */
    async pollOnce() {
      if (!this.pullingRefs.length) {
        this.stopPolling();
        return;
      }
      let items;
      try {
        items = (await fetchTrainDatasets()).data.items || [];
        this.pollFailures = 0;
      } catch (err) {
        this.pollFailures += 1;
        if (this.pollFailures >= PULL_POLL_MAX_FAILURES) this.stopPolling();
        return;
      }
      const byRef = new Map(items.map(item => [item.ref, item]));
      const pending = [];
      for (const ref of this.pullingRefs) {
        const item = byRef.get(ref);
        if (!item) continue; // 清单里已无该版本
        this.patchCache(ref, {
          cache_state: item.cache_state,
          cache_error: item.cache_error || null,
          cache_total: item.cache_total,
          cache_done: item.cache_done,
          cache: item.cache || undefined,
        });
        if (item.cache_state === 'ready') {
          this.$message.success(`${ref} 已下载到训练机本地`);
          continue;
        }
        if (item.cache_state === 'pulling') {
          pending.push(ref);
          continue;
        }
        // 既没就绪又没在下载：这一趟没成
        this.$notify({
          title: '下载没成',
          message: `${ref}：${item.cache_error || '平台没记下原因'}`,
          type: 'error',
          duration: 0,
        });
      }
      this.pullingRefs = pending;
      if (!pending.length) this.stopPolling();
    },

    /** 整版清单下载：图 + 标注 txt 逐文件预签名，前端流式拼 zip */
    async downloadManifest(row) {
      const version = row.pv ? row.pv.version : row.version;
      if (!version) return;
      await this.$refs.zipDl.run(async signal => {
        const manifest = (await fetchDatasetVersionDownloadManifest(
          version,
          this.datasetId,
          { signal },
        )).data || {};
        // 图在库里但对象存储里没有了：只提醒，不挡住其余文件
        if (Number(manifest.missing_images) > 0) {
          this.$message.warning(`${manifest.missing_images} 张图的文件在对象存储里不存在，已跳过`);
        }
        return manifest.files || [];
      }, `${row.ref || `v${version}`}.zip`);
    },


    /** 「标注项目」为空时悬停的缘由：平台上没记录 vs 没有关联项目 */
    noProjectReason(row) {
      return row.pv
        ? '这一版没有关联的标注项目'
        : '平台上还没有这一版的记录';
    },


    /** 批次记录入口：开弹窗时重取一次（登记完不用刷页面就能看到新批次与预标注状态） */
    async openBatchLog() {
      this.batchLog.visible = true;
      await this.loadBatchLog();
    },

    async loadBatchLog() {
      this.batchLog.loading = true;
      try {
        await this.loadBatches();
      } finally {
        this.batchLog.loading = false;
      }
    },

    /** 流水线状态档位（与数据列表同一份词表，避免两处各叫各的） */
    stageLabel(stage) {
      return (PIPELINE_STATES[stage] || {}).label || '-';
    },

    stageTag(stage) {
      return (PIPELINE_STATES[stage] || {}).tag || 'info';
    },

    /** 预标注任务行：派发/起止/失败原因（与批次弹窗、登记向导共用一份文案） */
    preannotateText(job) {
      return preAnnotateStatusText(job);
    },

    /** 预标注回执行：写回 LS 的张数与附注；没完成给空串，行里自然隐藏 */
    receiptOf(row) {
      return preAnnotateReceiptText((row.preannotate_job || {}).receipt);
    },

    /** 版本区间：自 v70（还在数据集里）/ v70→v79（剔除于 v80）；多段并列 */
    intervalText(intervals) {
      return (intervals || [])
        .map(iv => (iv.removed ? `v${iv.added}→v${iv.removed}` : `自 v${iv.added}`))
        .join('、');
    },

    /** 发布弹窗的批次候选 */
    async loadBatches() {
      try {
        const rows = (await fetchDatasetImagesStats(this.datasetId)).data || [];
        this.batches = rows;
        this.batchByName = {};
        for (const r of rows) this.batchByName[r.batch] = r;
      } catch (err) {
        // 拿不到批次不影响版本列表
      }
    },

    async loadInventory() {
      this.loadingInventory = true;
      try {
        this.inventory = (await fetchCacheInventory()).data;
        this.selected = [];
      } catch (err) {
        // 提示由响应拦截器统一弹出；保留上一次的数据
      } finally {
        this.loadingInventory = false;
      }
    },

    // ====== 展示辅助 ======

    cacheMeta(row) {
      // 状态是空串时按未下载对待
      return CACHE_META[row.cache_state] || CACHE_META.missing;
    },

    versionStatusMeta(row) {
      const status = row.pv && row.pv.status;
      const map = {
        publishing: { label: '发布中', tag: 'warning', hint: '版本还在导出/收口，暂不可训练或下载' },
        ready: { label: '已发布', tag: 'success', hint: null },
        failed: { label: '发布失败', tag: 'danger', hint: row.pv && row.pv.error_message },
        deprecated: { label: '已弃用', tag: 'info', hint: '历史引用可读，不再用于新训练' },
      };
      return map[status] || { label: '-', tag: 'info', hint: '历史版本没有版本目录行' };
    },

    localOf(row) {
      return this.localByRef[row.ref];
    },

    formatBytes,

    formatTime(value) {
      return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
    },

    /** 批次记录里的登记时间：列窄只留到分，完整时间放 title 悬停看 */
    shortTime(value) {
      return value ? dayjs(value).format('MM-DD HH:mm') : '-';
    },

    dash(value) {
      return value === null || value === undefined || value === '' ? '-' : value;
    },

    /** 结果清单里一项叫什么：版本给引用，权重给模型 ID，共享内容给 etag 的前几位 */
    itemLabel(item) {
      if (item.kind === 'version') return item.ref;
      if (item.kind === 'model') return `权重 ${item.model_id}`;
      return `共享内容 ${String(item.etag || '').slice(0, 12)}`;
    },

    // ====== 回收 ======

    /** 判定标签：可回收＝自动回收会清；可清理＝政策上保留但可手动清；保留＝不动 */
    verdictLabel(row) {
      if (row.verdict === 'reclaimable') return '可回收';
      if (row.forceable) return '可清理';
      return '保留';
    },

    /** 勾得动的两种：判定为可回收的，以及政策类保留、可手动清理的 */
    selectable(row) {
      return row.verdict === 'reclaimable' || row.forceable === true;
    },

    handleSelectionChange(rows) {
      this.selected = rows || [];
    },

    async handleReclaim() {
      const versions = this.selected.filter(row => row.kind === 'version').map(row => row.ref);
      const models = this.selected.filter(row => row.kind === 'model').map(row => row.model_id);
      const forcedRefs = this.selected.filter(row => row.forceable).map(row => row.ref);
      const expected = this.expectedBytes;
      try {
        await this.$confirm(
          this.confirmText(versions, models, expected, forcedRefs),
          '回收本地缓存',
          { type: 'warning', confirmButtonText: '确定回收', cancelButtonText: '取消' },
        );
      } catch (err) {
        return;
      }
      this.reclaiming = true;
      this.result = null;
      try {
        // 勾中的未必都会被删；pool_orphans 恒清，勾了保留项才带 force_policy
        this.result = (await deleteCache({
          versions,
          models,
          pool_orphans: true,
          force_policy: forcedRefs.length > 0,
        })).data;
        await this.loadInventory();
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.reclaiming = false;
      }
    },

    /** 确认文案：写清删什么、释放多少；手动清理的保留项单独点名 */
    confirmText(versions, models, expected, forcedRefs = []) {
      const parts = [];
      if (versions.length) parts.push(`${versions.length} 个数据集版本`);
      if (models.length) parts.push(`${models.length} 份基础权重`);
      if (this.pool.sweepable_count) parts.push(`无引用内容 ${this.pool.sweepable_count} 项`);
      let text = `将删除 ${parts.join('、')}。`;
      if (forcedRefs.length) {
        const names = forcedRefs.slice(0, 3).join('、') + (forcedRefs.length > 3 ? ' 等' : '');
        text += `其中 ${forcedRefs.length} 个仍被判为保留（${names}）：本次按手动清理处理。`;
      }
      if (expected) {
        text += `预计释放 ${formatBytes(expected)}。`;
      } else {
        text += '所选内容均与其他版本共用，不释放空间。';
      }
      return `${text}删除后需从 MinIO 重新下载。确认？`;
    },
  },
};
</script>

<style scoped>
.table-card {
  margin-bottom: 16px;
}

/* 标注项目直达标注平台 */
.ls-link {
  color: #409EFF;
  text-decoration: none;
}

/* 批次记录弹窗：表头一行（计数 + 刷新）、单元格里的附注小字 */
.batch-log-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

/* 列宽按百分比自适应弹窗宽度，单元格一律单行：放不下省略号，悬停看全文 */
.batch-log-table .cell {
  white-space: nowrap;
}

.batch-log-note {
  color: #909399;
}

.ls-link:hover {
  text-decoration: underline;
}

/* 列头口径说明图标 */
.header-hint {
  margin-left: 4px;
  color: #909399;
  font-size: 12px;
  cursor: help;
  vertical-align: middle;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.keyword-input {
  width: 220px;
}

.dataset-picker {
  width: 200px;
}

.total-count {
  color: #909399;
  font-size: 13px;
}

/* 页头统计 */
.stat-row {
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  padding: 4px 0 16px;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-label {
  font-size: 12px;
  color: #909399;
}

.stat-value {
  font-size: 18px;
  color: #303133;
  font-variant-numeric: tabular-nums;
}

.notice {
  margin-bottom: 12px;
}

.pool-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 12px;
}

.list-line {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding-top: 8px;
}

.list-label {
  flex: none;
  font-size: 12px;
  color: #909399;
}

.mono {
  font-family: Consolas, Monaco, monospace;
  color: #606266;
}

.cell-sub {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.ellipsis-cell {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muted {
  color: #c0c4cc;
}

/* 旧格式标签：紧跟判定标签 */
.legacy-tag {
  margin-left: 4px;
}

.danger-text {
  color: #f56c6c;
}

.split-ratios { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 6px; }
.split-ratio-item { display: inline-flex; align-items: center; gap: 3px; font-size: 12px; color: #606266; }
.split-ratio-item .el-input-number { width: 92px; }
.split-ratio-sum { font-size: 12px; color: #67C23A; }
.split-ratio-sum.bad { color: #F56C6C; }
.publish-hint {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.publish-current {
  padding: 10px 12px;
  background: #F4F4F5;
  border-radius: 4px;
  margin-bottom: 14px;
  color: #606266;
}

.publish-progress {
  padding: 16px 22px;
}

.publish-progress-title {
  font-size: 16px;
  color: #303133;
  margin-bottom: 14px;
}

.publish-progress-target {
  margin-left: 10px;
  font-weight: 600;
}

.publish-progress-line {
  margin: 0 0 8px;
  color: #606266;
}

.publish-progress-sub {
  margin: 0 0 8px;
  color: #909399;
  font-size: 12px;
}

.publish-progress-ok {
  color: #67C23A;
  font-weight: 600;
}

.publish-progress-dead {
  margin-top: 10px;
  color: #E6A23C;
}

.publish-cols {
  display: flex;
  gap: 14px;
  margin-bottom: 14px;
}

.publish-col {
  flex: 1;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.publish-col-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid #EBEEF5;
  background: #FAFAFA;
}

.publish-col-title {
  font-weight: 600;
  margin-right: auto;
}

.publish-col-body {
  flex: 1;
  max-height: 300px;
  overflow-y: auto;
  padding: 4px 10px;
}

.publish-col-body .el-checkbox-group {
  display: block;
}

.publish-row {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 6px 0;
  margin: 0;
}

.publish-row .el-checkbox__label {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  line-height: 1.7;
}

.publish-row-line1 {
  display: flex;
  align-items: center;
}

.publish-row-line2 {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.publish-batch {
  font-family: "SFMono-Regular", Consolas, monospace;
  margin-right: 8px;
}

.publish-sub {
  color: #909399;
  font-size: 12px;
  margin-right: 8px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.publish-conf {
  color: #67C23A;
  font-size: 12px;
  margin-right: 8px;
  white-space: nowrap;
}

.publish-conf.publish-unconf {
  color: #E6A23C;
}

.publish-preview-unconf {
  margin-top: 4px;
  color: #E6A23C;
}

.publish-empty {
  color: #C0C4CC;
  text-align: center;
  padding: 18px 0;
}

.publish-col-foot {
  padding: 8px 10px;
  border-top: 1px solid #EBEEF5;
  color: #606266;
  font-size: 12px;
}

.publish-preview {
  padding: 10px 12px;
  background: #ECF5FF;
  border: 1px solid #D9ECFF;
  border-radius: 4px;
  margin-bottom: 14px;
  color: #409EFF;
}

.publish-preview.publish-overlap {
  background: #FDF6EC;
  border-color: #FAECD8;
  color: #E6A23C;
}

.publish-preview-inst {
  margin-top: 4px;
  font-size: 12px;
  color: #606266;
}

/* 条件筛选折叠区 */
.publish-cond {
  border: 1px dashed #B3D8FF;
  border-radius: 4px;
  background: #F4F9FF;
  padding: 8px 10px;
  margin-bottom: 10px;
}

.publish-cond-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #1F5FBF;
  margin-bottom: 4px;
}

.publish-cond-fold {
  margin-left: auto;
  font-weight: 400;
  color: #909399;
  cursor: pointer;
}

.publish-cond-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 5px 0;
  font-size: 12px;
  color: #606266;
  flex-wrap: wrap;
}

.publish-cond-lab {
  color: #909399;
}

.publish-cond-sep {
  width: 1px;
  height: 16px;
  background: #DCDFE6;
  margin: 0 4px;
}

.publish-cond-check {
  margin-right: 0;
}

.publish-cond-check .el-checkbox__label {
  font-size: 12px;
  padding-left: 6px;
}

.publish-cond-note {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.publish-cond-note b {
  color: #1F5FBF;
}

/* 批次行标签/实例列 */
.publish-tag {
  display: inline-block;
  padding: 0 6px;
  font-size: 11px;
  background: #ECF5FF;
  color: #409EFF;
  border-radius: 8px;
  white-space: nowrap;
}

.publish-tags-empty {
  color: #C0C4CC;
  font-size: 12px;
}

.publish-inst {
  color: #606266;
  font-size: 12px;
  white-space: nowrap;
  margin-left: 4px;
}

/* 图片预览 dialog */
.pv-stats {
  padding: 8px 12px;
  background: #F5F7FA;
  border-radius: 4px;
  color: #606266;
  font-size: 12.5px;
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.pv-stats b {
  color: #303133;
}

.pv-groups {
  max-height: 480px;
  overflow: auto;
}

.pv-group {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  margin-bottom: 12px;
  overflow: hidden;
}

.pv-group-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #F5F7FA;
  border-bottom: 1px solid #EBEEF5;
  font-size: 12.5px;
  cursor: pointer;
}

.pv-batch {
  font-family: "SFMono-Regular", Consolas, monospace;
  font-weight: 600;
  color: #303133;
}

.pv-meta {
  color: #909399;
}

.pv-expand {
  margin-left: auto;
  color: #409EFF;
  font-size: 12px;
}

.pv-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 6px;
  padding: 10px 12px;
  min-height: 60px;
}

.pv-thumb-wrap {
  position: relative;
}

.pv-unlabeled {
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: 10px;
  padding: 0 4px;
  background: rgba(144, 147, 153, .85);
  color: #fff;
  border-radius: 2px;
}

.pv-thumb {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 3px;
  background: #F0F2F5;
  cursor: pointer;
  display: block;
}

.pv-empty {
  grid-column: 1 / -1;
  color: #C0C4CC;
  text-align: center;
  font-size: 12px;
  padding: 16px 0;
}

.pv-page {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 4px 0 12px;
  color: #606266;
  font-size: 12.5px;
}

.class-plan-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
}

.class-plan-warn {
  margin: 8px 0;
}

.class-plan-files {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 14px;
}

.class-plan-files h4 {
  margin: 0 0 6px;
  font-size: 12.5px;
  color: #606266;
}

.class-plan-files pre {
  margin: 0;
  padding: 10px;
  max-height: 220px;
  overflow: auto;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.6;
}
</style>
